import type { Components } from '@/api/generated/api';
import { SchoolLevelLabelMode } from '@/api/generated/api.enum';
import { ISelectOption } from '@/interfaces';
import { buildSchoolLevelOptions } from '@/utils/schoolLevel.utils';
import { computed, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';

export interface IUseSchoolLevels {
  /** Sorted + deduplicated global school levels. */
  schoolLevels: ComputedRef<number[]>;
  /** The effective label mode (defaults to `adjusted` when the source is undefined). */
  labelMode: ComputedRef<Components.Schemas.SchoolLevelLabelMode>;
  /** Convenience boolean: `labelMode === raw`. */
  useRawSchoolLevelLabels: ComputedRef<boolean>;
  /** Picker/tab options — VALUE is always the global level; only the LABEL follows the mode. */
  schoolLevelOptions: ComputedRef<ISelectOption<number>[]>;
  /** Label for a single global level (raw number, or the adjusted 1..N rank, or the pre-school label). */
  getSchoolLevelLabel: (level: number) => string;
  /** Highest configured level (used e.g. for "bump to next" affordances), or undefined when empty. */
  lastSchoolLevel: ComputedRef<number | undefined>;
  /** Whether a level directly above `level` is configured (i.e. a class can be bumped up). */
  hasNextSchoolLevel: (level: number) => boolean;
}

/**
 * Single presentation layer for school levels: given a source of raw levels and a label mode, it produces the sorted
 * unique level list, the picker options, a single-level label lookup, and the bump helpers. Option VALUES are always
 * the global level; only LABELS follow the mode (raw = the real number, adjusted = renumbered 1..N within the range,
 * pre-school 0 kept special). The source-specific composables — the current tenant/template-set
 * ({@link import('./useSchoolLevelsBySchoolType')}) and a resolved provider scope
 * ({@link import('./provider/useResolvedSchoolLevels')}) — delegate here, as does any call site that already holds an
 * explicit (levels, labelMode) pair (e.g. the import dialog labelling a source school type in the tenant's mode). This
 * is the ONLY place the display math lives.
 */
export function useSchoolLevels(
  getLevels: () => number[] | undefined,
  getLabelMode: () => Components.Schemas.SchoolLevelLabelMode | undefined,
): IUseSchoolLevels {
  const { t } = useI18n();

  const schoolLevels = computed<number[]>(() => [...new Set(getLevels() ?? [])].sort((a, b) => a - b));
  const labelMode = computed<Components.Schemas.SchoolLevelLabelMode>(
    () => getLabelMode() ?? SchoolLevelLabelMode.adjusted,
  );
  const useRawSchoolLevelLabels = computed<boolean>(() => labelMode.value === SchoolLevelLabelMode.raw);

  const schoolLevelOptions = computed<ISelectOption<number>[]>(() =>
    buildSchoolLevelOptions({
      schoolLevels: schoolLevels.value,
      t,
      useRawSchoolLevelLabels: useRawSchoolLevelLabels.value,
    }),
  );

  const getSchoolLevelLabel = (level: number): string =>
    schoolLevelOptions.value.find((option) => option.value === level)?.label ?? String(level);

  const lastSchoolLevel = computed<number | undefined>(() =>
    schoolLevels.value.length > 0 ? Math.max(...schoolLevels.value) : undefined,
  );

  const hasNextSchoolLevel = (level: number): boolean => schoolLevels.value.includes(level + 1);

  return {
    schoolLevels,
    labelMode,
    useRawSchoolLevelLabels,
    schoolLevelOptions,
    getSchoolLevelLabel,
    lastSchoolLevel,
    hasNextSchoolLevel,
  };
}
